export type ImageViewport =
	| "thumbnail"
	| "productPage"
	| "cart"
	| "checkoutDocument"
	| "orders";

export interface ImageTransformConfig {
	width: number;
	height?: number;
	fit?: "cover" | "contain" | "scale-down";
}

export interface ResponsiveImageConfig {
	desktop: ImageTransformConfig;
	mobile: ImageTransformConfig;
}

const MOBILE_BREAKPOINT_PX = 768;

export const IMAGE_VIEWPORTS: Record<ImageViewport, ResponsiveImageConfig> = {
	thumbnail: {
		desktop: { width: 144, height: 144, fit: "contain" },
		mobile: { width: 128, height: 128, fit: "contain" },
	},
	productPage: {
		desktop: { width: 292, height: 292, fit: "scale-down" },
		mobile: { width: 178, height: 178, fit: "scale-down" },
	},
	cart: {
		desktop: { width: 70, height: 90, fit: "contain" },
		mobile: { width: 70, height: 90, fit: "contain" },
	},
	checkoutDocument: {
		desktop: { width: 184, height: 184, fit: "contain" },
		mobile: { width: 184, height: 184, fit: "contain" },
	},
	orders: {
		desktop: { width: 76, height: 61, fit: "contain" },
		mobile: { width: 76, height: 61, fit: "contain" },
	},
};

/**
 * Returns the width and height for a given viewport and device.
 * Useful for setting explicit img dimensions to prevent layout shift (CLS).
 */
export function getImageDimensions(
	viewport: ImageViewport,
	device: "desktop" | "mobile" = "desktop",
): { width: number; height: number | undefined } {
	const config = IMAGE_VIEWPORTS[viewport][device];
	return { width: config.width, height: config.height };
}

/**
 * Checks if a URL is an external URL (starts with http:// or https://)
 */
function isExternalUrl(url: string): boolean {
	return url.startsWith("http://") || url.startsWith("https://");
}

const PUBLIC_STORAGE_PREFIX = "https://storage.googleapis.com/";

/**
 * The CDN bucket served under its own hostname (`CDN_PUBLIC_BASE_URL` on the
 * server): production `cdn.dransay.com`, staging `cdn-staging.dransay.me`.
 */
const PUBLIC_CDN_ORIGIN = /^https:\/\/cdn(?:-staging)?\.dransay\.(?:com|me)\//;

/**
 * Re-encode each `/`-separated segment of a decoded GCS object path.
 *
 * `decodeURIComponent` turns a key's `%3F` / `%23` back into literal `?` / `#`,
 * which `stripPublicStorageQuery` would then read as the start of a query or
 * fragment and truncate the key with it. Re-encoding keeps those bytes inside
 * the path.
 *
 * It also matches what `@dransay/api` emits for the same object, so a Firebase
 * envelope unwrapped here and a bare URL sanitized server-side resolve to one
 * Cloudflare URL instead of two cache entries.
 */
function encodeObjectPath(path: string): string {
	return path.split("/").map(encodeURIComponent).join("/");
}

/**
 * Drop the query string and fragment from a bare GCS object URL.
 *
 * Product image URLs carry a `?productid=<firestore-id>` suffix appended during
 * ETL ingestion to keep `image_information.key` unique across products that
 * share one source image. Once folded into the `/cdn-cgi/image/<options>/<url>`
 * path the `?` becomes `%3F`, which trips a Cloudflare WAF managed rule: the
 * edge answers `cf-mitigated: challenge` with HTTP 403 and the `<img>` renders
 * blank because it cannot solve the challenge.
 *
 * Every current tRPC boundary already strips this server-side, so this is a
 * second line of defence rather than the load-bearing fix. It lives here
 * because this module is where the WAF-sensitive URL is actually assembled:
 * whatever reaches `buildCloudflareUrl` ends up percent-encoded into a path
 * that Cloudflare inspects, no matter which endpoint supplied it.
 *
 * Objects in our public buckets never need a query string, whether served from
 * `storage.googleapis.com` or from the CDN hostname. Other hosts pass through
 * untouched because their query may carry a signature.
 */
function stripPublicStorageQuery(url: string): string {
	if (!url.startsWith(PUBLIC_STORAGE_PREFIX) && !PUBLIC_CDN_ORIGIN.test(url)) {
		return url;
	}
	const delimiter = url.search(/[?#]/);
	return delimiter === -1 ? url : url.slice(0, delimiter);
}

/**
 * Unwrap a Firebase Storage URL into the bare GCS object URL.
 *
 * Firebase Storage URLs of the shape
 *   https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encoded-path>?alt=media&token=...
 * carry `Cache-Control: private, max-age=14400` on the response (Firebase default
 * for tokened URLs). Cloudflare Image Resizing echoes that header to clients,
 * which prevents our Cache Rule's `browser_ttl: override_origin 30d` from
 * taking effect and emits a `cf-images 299 cache-control is too restrictive`
 * warning (DEVOPS-1107 diagnosis).
 *
 * Rewriting to the bare `storage.googleapis.com/<bucket>/<path>` URL lets
 * Cloudflare apply our cache overrides cleanly.
 *
 * DEVOPS-1122 bucket swap: `gs://cannation/shop/images/*` is NOT publicly
 * readable via bare GCS URL (Firebase Storage Rules grant access only via
 * firebasestorage.googleapis.com). Bare URLs against cannation return 403.
 * Bare URLs against the dedicated public bucket gs://shop-dransay-images-production
 * return 200 (allUsers:objectViewer at bucket level). When the input Firebase
 * envelope targets cannation/shop/images/, redirect to the dedicated bucket
 * (path prefix `shop/images/` is stripped because the new bucket holds the
 * objects at root, matching how the STS replication job stripped the source
 * path prefix on copy).
 *
 * Bare GCS URLs are additionally sanitized by `stripPublicStorageQuery`.
 * Unknown / non-Firebase, non-GCS URLs pass through unchanged.
 */
export function stripFirebaseEnvelope(url: string): string {
	const m = url.match(
		/^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\/o\/([^?]+)/,
	);
	if (!m) return stripPublicStorageQuery(url);
	const bucket = m[1];
	const path = decodeURIComponent(m[2]);
	if (bucket === "cannation" && path.startsWith("shop/images/")) {
		const objectKey = path.slice("shop/images/".length);
		return stripPublicStorageQuery(
			`https://storage.googleapis.com/shop-dransay-images-production/${encodeObjectPath(objectKey)}`,
		);
	}
	return stripPublicStorageQuery(
		`https://storage.googleapis.com/${bucket}/${encodeObjectPath(path)}`,
	);
}

function buildTransformOptions(config: ImageTransformConfig): string {
	const options: string[] = [`width=${config.width}`];
	if (config.height) {
		options.push(`height=${config.height}`);
	}
	if (config.fit) {
		options.push(`fit=${config.fit}`);
	}
	options.push("format=auto", "quality=auto");
	return options.join(",");
}

function buildCloudflareUrl(
	sourceUrl: string,
	config: ImageTransformConfig,
	cloudflareBaseUrl: string,
): string {
	const options = buildTransformOptions(config);
	const bareUrl = stripFirebaseEnvelope(sourceUrl);
	const encodedUrl = encodeURIComponent(bareUrl);
	return `${cloudflareBaseUrl}/cdn-cgi/image/${options}/${encodedUrl}`;
}

function createScaledConfig(
	config: ImageTransformConfig,
	scale: number,
): ImageTransformConfig {
	return {
		...config,
		width: config.width * scale,
		height: config.height ? config.height * scale : undefined,
	};
}

function addWidthEntry(
	entries: Map<number, string>,
	sourceUrl: string,
	config: ImageTransformConfig,
	cloudflareBaseUrl: string,
): void {
	const url = buildCloudflareUrl(sourceUrl, config, cloudflareBaseUrl);
	entries.set(config.width, `${url} ${config.width}w`);
}

export interface ResponsiveImageUrls {
	src: string;
	srcSet: string;
	sizes: string;
}

/**
 * Resolves a site-relative asset path to an absolute origin URL.
 */
export function resolveSiteAssetUrl(path: string, siteOrigin: string): string {
	if (isExternalUrl(path)) {
		return path;
	}
	const normalizedOrigin = siteOrigin.replace(/\/$/, "");
	const normalizedPath = path.startsWith("/") ? path : `/${path}`;
	return `${normalizedOrigin}${normalizedPath}`;
}

/**
 * Builds responsive image URLs with explicit desktop/mobile transform configs.
 */
export function buildCustomResponsiveImageUrls(
	sourceUrl: string,
	desktop: ImageTransformConfig,
	mobile: ImageTransformConfig,
	cloudflareBaseUrl: string,
): ResponsiveImageUrls {
	const widthEntries = new Map<number, string>();
	addWidthEntry(widthEntries, sourceUrl, mobile, cloudflareBaseUrl);
	addWidthEntry(widthEntries, sourceUrl, desktop, cloudflareBaseUrl);
	addWidthEntry(
		widthEntries,
		sourceUrl,
		createScaledConfig(mobile, 2),
		cloudflareBaseUrl,
	);
	addWidthEntry(
		widthEntries,
		sourceUrl,
		createScaledConfig(desktop, 2),
		cloudflareBaseUrl,
	);
	const srcSetEntries = Array.from(widthEntries.entries())
		.sort(([a], [b]) => a - b)
		.map(([, entry]) => entry);
	const sizes = `(max-width: ${MOBILE_BREAKPOINT_PX}px) ${mobile.width}px, ${desktop.width}px`;
	return {
		src: buildCloudflareUrl(sourceUrl, desktop, cloudflareBaseUrl),
		srcSet: srcSetEntries.join(", "),
		sizes,
	};
}

/**
 * Builds Cloudflare-transformed URLs for a public static asset path.
 */
export function buildStaticAssetImageUrls(
	relativePath: string,
	desktop: ImageTransformConfig,
	cloudflareBaseUrl?: string,
	mobile?: ImageTransformConfig,
): ResponsiveImageUrls {
	if (!cloudflareBaseUrl) {
		return { src: relativePath, srcSet: "", sizes: "" };
	}
	const absoluteSourceUrl = resolveSiteAssetUrl(
		relativePath,
		cloudflareBaseUrl,
	);
	const mobileConfig = mobile ?? desktop;
	return buildCustomResponsiveImageUrls(
		absoluteSourceUrl,
		desktop,
		mobileConfig,
		cloudflareBaseUrl,
	);
}

/**
 * Builds responsive image URLs with srcset for optimal browser selection.
 * Uses Cloudflare Transform Images to serve different sizes for mobile/desktop.
 *
 * @see https://developers.cloudflare.com/images/transform-images/make-responsive-images/
 *
 * @param sourceUrl - The original image URL (e.g., Firebase Storage URL)
 * @param viewport - The viewport configuration to apply
 * @param cloudflareBaseUrl - The Cloudflare Transform Images base URL
 * @returns Object with src, srcSet, and sizes for responsive images
 */
export function buildResponsiveImageUrls(
	sourceUrl: string | null | undefined,
	viewport: ImageViewport,
	cloudflareBaseUrl?: string,
): ResponsiveImageUrls | undefined {
	if (!sourceUrl) return undefined;
	const { desktop, mobile } = IMAGE_VIEWPORTS[viewport];
	if (!cloudflareBaseUrl || !isExternalUrl(sourceUrl)) {
		return { src: sourceUrl, srcSet: "", sizes: "" };
	}
	return buildCustomResponsiveImageUrls(
		sourceUrl,
		desktop,
		mobile,
		cloudflareBaseUrl,
	);
}

/**
 * Builds a single Cloudflare Transform Images URL (non-responsive).
 * Use buildResponsiveImageUrls for responsive images with srcset.
 *
 * @param sourceUrl - The original image URL (e.g., Firebase Storage URL)
 * @param viewport - The viewport configuration to apply
 * @param cloudflareBaseUrl - The Cloudflare Transform Images base URL
 * @param device - Which device config to use (defaults to desktop)
 * @returns The transformed Cloudflare URL or original URL if not applicable
 */
export function buildProductImageUrl(
	sourceUrl: string | null | undefined,
	viewport: ImageViewport,
	cloudflareBaseUrl?: string,
	device: "desktop" | "mobile" = "desktop",
): string | undefined {
	if (!sourceUrl) return undefined;
	if (!cloudflareBaseUrl || !isExternalUrl(sourceUrl)) return sourceUrl;
	const config = IMAGE_VIEWPORTS[viewport][device];
	return buildCloudflareUrl(sourceUrl, config, cloudflareBaseUrl);
}
